import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Rocket } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { PublicLayout } from "@/components/PublicLayout";
import { Field } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY = { denominazione: "", piva: "", nome: "", email: "", password: "", telefono: "", privacy: false, termini: false };
const FIELDS = [["denominazione", "Ragione sociale", "text", "sm:col-span-2"], ["piva", "Partita IVA", "text"], ["telefono", "Telefono", "tel"],
  ["nome", "Nome e cognome del referente", "text"], ["email", "Email (sar\u00e0 il tuo accesso)", "email"], ["password", "Password (min. 8 caratteri, lettere e numeri)", "password", "sm:col-span-2"]];
const PLUS = ["Tutte le funzioni attive per 30 giorni", "Fatture, DDT, preventivi, ordini, magazzino e cassa", "Agenti di vendita dal tablet", "Nessuna carta di credito richiesta",
  "File XML ufficiale controllato; l'invio allo SdI si attiva con il piano"];

export default function Prova() {
  useSeo("Prova gratuita 30 giorni", "Prova gratis per 30 giorni FatturazioneGestionale: fatturazione elettronica, magazzino, DDT, corrispettivi e agenti di vendita. Nessuna carta di credito.", "/prova");
  const [sp] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY, forfettario: sp.get("offerta") === "forfettari" });
  const [busy, setBusy] = useState(false);
  const { reload } = useAuth();
  const nav = useNavigate();
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.privacy || !f.termini) return toast.error("Accetta informativa privacy e termini di servizio");
    setBusy(true);
    try {
      await api.post("/trial/signup", f);
      await reload();
      toast.success("Prova gratuita attivata: benvenuto!");
      nav("/app/impostazioni");
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <PublicLayout testid="trial-page">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr]">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Prova gratuita</div>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">30 giorni gratis, tutto incluso</h1>
          <p className="mt-4 text-base text-slate-600">Crea il tuo account in un minuto e inizia subito a lavorare. Alla fine della prova i dati restano tuoi e consultabili.</p>
          <ul className="mt-8 space-y-3 text-sm text-slate-700">
            {PLUS.map((t) => <li key={t} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{t}</li>)}
          </ul>
        </div>
        <form onSubmit={submit} data-testid="trial-form" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, l, type, cls]) => (
              <Field key={k} label={l} className={cls}><Input required type={type} data-testid={`trial-${k}-input`} value={f[k]} onChange={(e) => set(k, e.target.value)} /></Field>
            ))}
          </div>
          <label className="mt-4 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-slate-800"><Checkbox data-testid="trial-forfettario-checkbox" checked={f.forfettario} onCheckedChange={(v) => set("forfettario", !!v)} /><span>Sono in <b>regime forfettario</b>: voglio l'offerta a 4 €/mese per il primo anno</span></label>
          {[["privacy", <>Ho letto l'<Link to="/privacy" target="_blank" className="text-blue-700 underline">informativa privacy</Link></>], ["termini", <>Accetto i <Link to="/termini" target="_blank" className="text-blue-700 underline">termini di servizio</Link></>]].map(([k, l]) => (
            <label key={k} className="mt-4 flex items-start gap-2 text-sm text-slate-700"><Checkbox data-testid={`trial-${k}-checkbox`} checked={f[k]} onCheckedChange={(v) => set(k, !!v)} /><span>{l}</span></label>
          ))}
          <Button type="submit" disabled={busy} data-testid="trial-submit-button" className="mt-6 h-12 w-full bg-blue-600 text-base hover:bg-blue-700"><Rocket className="mr-2 h-4 w-4" />{busy ? "Attivazione\u2026" : "Attiva la prova gratuita"}</Button>
          <p className="mt-3 text-center text-xs text-slate-500">Hai già un account? <Link to="/login" className="text-blue-700 underline">Accedi</Link></p>
        </form>
      </div>
    </PublicLayout>
  );
}
